import React from 'react';
import { toast, ToastOptions, Id } from 'react-toastify';
import { logError, ErrorLogRequest, getBrowserInfo } from '../api/errorLogAPI';
import { captureError } from './sentry';
import { safeGetItem, safeGetObject } from '@mediatechgroup/app-version';

// Global state for error context - will be set by ErrorToastProvider
let openReportModal: ((context: ErrorContext) => void) | null = null;

export interface ErrorContext {
  errorLogId?: string;
  errorMessage: string;
  errorCode?: string;
  pageUrl?: string;
  pageTitle?: string;
  actionContext?: string;
  componentName?: string;
  apiEndpoint?: string;
  apiMethod?: string;
  responseStatus?: number;
  responseError?: Record<string, unknown>;
  additionalContext?: Record<string, unknown>;
}

export interface ErrorToastOptions extends ToastOptions {
  // Error context for logging
  errorCode?: string;
  actionContext?: string;
  componentName?: string;
  apiEndpoint?: string;
  apiMethod?: string;
  requestPayload?: Record<string, unknown>;
  responseStatus?: number;
  responseError?: Record<string, unknown>;
  additionalContext?: Record<string, unknown>;
  // Control options
  skipLogging?: boolean;
  showReportButton?: boolean;
}

// Set the modal opener function (called by ErrorToastProvider)
export const setReportModalOpener = (opener: (context: ErrorContext) => void) => {
  openReportModal = opener;
};

// Custom toast content with Report Issue button
const ErrorToastContent: React.FC<{
  message: string;
  errorContext: ErrorContext;
  showReportButton: boolean;
}> = ({ message, errorContext, showReportButton }) => {
  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (openReportModal) {
      openReportModal(errorContext);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span>{message}</span>
      {showReportButton && openReportModal && (
        <button
          onClick={handleReportClick}
          className="text-xs text-white/80 hover:text-white underline underline-offset-2 text-left transition-colors"
        >
          Report this issue
        </button>
      )}
    </div>
  );
};

/**
 * Enhanced error toast that logs errors and provides a "Report Issue" option
 *
 * Usage:
 * ```
 * import { errorToast } from '../utils/errorToast';
 *
 * // Simple usage (replaces toast.error)
 * errorToast('Something went wrong');
 *
 * // With context for better error tracking
 * errorToast('Failed to save payment', {
 *   actionContext: 'Saving payment method',
 *   apiEndpoint: '/api/payments',
 *   apiMethod: 'POST',
 *   responseStatus: 500,
 *   responseError: { message: 'Internal server error' }
 * });
 *
 * // Skip logging (for non-critical errors)
 * errorToast('Invalid input', { skipLogging: true });
 * ```
 */
export const errorToast = async (
  message: string,
  options: ErrorToastOptions = {}
): Promise<Id> => {
  const {
    errorCode,
    actionContext,
    componentName,
    apiEndpoint,
    apiMethod,
    requestPayload,
    responseStatus,
    responseError,
    additionalContext,
    skipLogging = false,
    showReportButton = true,
    ...toastOptions
  } = options;

  // Build error context
  const errorContext: ErrorContext = {
    errorMessage: message,
    errorCode,
    pageUrl: window.location.href,
    pageTitle: document.title,
    actionContext,
    componentName,
    apiEndpoint,
    apiMethod,
    responseStatus,
    responseError,
    additionalContext,
  };

  // Log error to backend (async, non-blocking)
  if (!skipLogging) {
    const token = safeGetItem('authToken');
    const userDetails = safeGetObject<Record<string, any>>('userDetails');

    const logData: ErrorLogRequest = {
      errorMessage: message,
      errorCode,
      pageUrl: window.location.href,
      pageTitle: document.title,
      actionContext,
      componentName,
      apiEndpoint,
      apiMethod,
      requestPayload,
      responseStatus,
      responseError,
      browserInfo: getBrowserInfo(),
      additionalContext,
      userId: userDetails.id,
      userEmail: userDetails.email,
      userRole: userDetails.role,
    };

    // Fire and forget - don't wait for logging to complete
    logError(token, logData)
      .then((result) => {
        if (result.success && result.data?.errorLogId) {
          // Update error context with log ID for linking to tickets
          errorContext.errorLogId = result.data.errorLogId;
        }
      })
      .catch((err) => {
        captureError(err, { component: 'ErrorToast', operation: 'logError' });
      });
  }

  // Show toast with custom content
  return toast.error(
    <ErrorToastContent
      message={message}
      errorContext={errorContext}
      showReportButton={showReportButton}
    />,
    {
      autoClose: 8000, // Slightly longer for error toasts
      closeOnClick: false, // Don't close on click so user can click "Report"
      ...toastOptions,
    }
  );
};

/**
 * Helper to extract error message from various error types
 */
export const getErrorMessage = (error: unknown, fallback = 'An unexpected error occurred'): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;

    // Axios error response
    if (err.response && typeof err.response === 'object') {
      const response = err.response as Record<string, unknown>;
      if (response.data && typeof response.data === 'object') {
        const data = response.data as Record<string, unknown>;
        if (typeof data.message === 'string') return data.message;
        if (typeof data.error === 'string') return data.error;
      }
    }

    // Direct message
    if (typeof err.message === 'string') return err.message;

    // Data.message
    if (err.data && typeof err.data === 'object') {
      const data = err.data as Record<string, unknown>;
      if (typeof data.message === 'string') return data.message;
    }
  }

  return fallback;
};

/**
 * Helper to extract error context from axios error
 */
export const getApiErrorContext = (
  error: unknown,
  actionContext?: string
): Partial<ErrorToastOptions> => {
  if (!error || typeof error !== 'object') return { actionContext };

  const err = error as Record<string, unknown>;
  const context: Partial<ErrorToastOptions> = { actionContext };

  // Axios error
  if (err.config && typeof err.config === 'object') {
    const config = err.config as Record<string, unknown>;
    context.apiEndpoint = config.url as string | undefined;
    context.apiMethod = config.method as string | undefined;

    // Try to get request data
    if (config.data) {
      try {
        context.requestPayload = typeof config.data === 'string'
          ? JSON.parse(config.data)
          : config.data as Record<string, unknown>;
      } catch {
        // Ignore parse errors
      }
    }
  }

  if (err.response && typeof err.response === 'object') {
    const response = err.response as Record<string, unknown>;
    context.responseStatus = response.status as number | undefined;

    if (response.data && typeof response.data === 'object') {
      context.responseError = response.data as Record<string, unknown>;
    }
  }

  return context;
};

// Export a default that matches the toast.error signature for easy migration
export default errorToast;
